import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CountrySelect,
  Icon,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import { createOperator, deleteOperator, listOperators, updateOperator } from "../api/dpp.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import { OPERATOR_IDENTIFIER_SCHEMES, OPERATOR_ROLES } from "../types.js";
import type {
  AccessGrantRole,
  EconomicOperator,
  OperatorBody,
  OperatorIdentifierScheme,
  OperatorRole,
} from "../types.js";
import { AudienceField } from "./AudienceField.js";

/** Form drafts ride as strings; blanks are dropped from the request body. */
interface OperatorDraft {
  role: OperatorRole;
  isDefault: boolean;
  legalName: string;
  tradeName: string;
  streetAddress: string;
  extendedAddress: string;
  locality: string;
  postalCode: string;
  region: string;
  countryIso: string;
  email: string;
  phone: string;
  website: string;
  identifierScheme: OperatorIdentifierScheme | "";
  identifierValue: string;
  eori: string;
  contactTier: "public" | "restricted";
  audience: AccessGrantRole[];
  mandateValidFrom: string;
  mandateValidUntil: string;
}

const EMPTY: OperatorDraft = {
  role: "importer",
  isDefault: true,
  legalName: "",
  tradeName: "",
  streetAddress: "",
  extendedAddress: "",
  locality: "",
  postalCode: "",
  region: "",
  countryIso: "",
  email: "",
  phone: "",
  website: "",
  identifierScheme: "",
  identifierValue: "",
  eori: "",
  contactTier: "public",
  audience: [],
  mandateValidFrom: "",
  mandateValidUntil: "",
};

/** Roles whose contact block the regulation puts on the public part of the passport. */
const PUBLIC_BY_DEFAULT: OperatorRole[] = ["manufacturer", "importer", "responsible_person"];

function draftOf(o: EconomicOperator): OperatorDraft {
  return {
    role: o.role,
    isDefault: o.isDefault,
    legalName: o.legalName,
    tradeName: o.tradeName ?? "",
    streetAddress: o.streetAddress ?? "",
    extendedAddress: o.extendedAddress ?? "",
    locality: o.locality ?? "",
    postalCode: o.postalCode ?? "",
    region: o.region ?? "",
    countryIso: o.countryIso,
    email: o.email ?? "",
    phone: o.phone ?? "",
    website: o.website ?? "",
    identifierScheme: o.identifierScheme ?? "",
    identifierValue: o.identifierValue ?? "",
    eori: o.eori ?? "",
    contactTier: o.contactTier,
    audience: o.audience ?? [],
    mandateValidFrom: o.mandateValidFrom ? o.mandateValidFrom.slice(0, 10) : "",
    mandateValidUntil: o.mandateValidUntil ? o.mandateValidUntil.slice(0, 10) : "",
  };
}

function bodyOf(d: OperatorDraft): OperatorBody {
  const opt = (v: string) => (v.trim() === "" ? undefined : v.trim());
  return {
    role: d.role,
    isDefault: d.isDefault,
    legalName: d.legalName.trim(),
    countryIso: d.countryIso.trim().toUpperCase(),
    contactTier: d.contactTier,
    audience: d.contactTier === "restricted" && d.audience.length > 0 ? d.audience : null,
    ...(opt(d.tradeName) ? { tradeName: opt(d.tradeName)! } : {}),
    ...(opt(d.streetAddress) ? { streetAddress: opt(d.streetAddress)! } : {}),
    ...(opt(d.extendedAddress) ? { extendedAddress: opt(d.extendedAddress)! } : {}),
    ...(opt(d.locality) ? { locality: opt(d.locality)! } : {}),
    ...(opt(d.postalCode) ? { postalCode: opt(d.postalCode)! } : {}),
    ...(opt(d.region) ? { region: opt(d.region)! } : {}),
    ...(opt(d.email) ? { email: opt(d.email)! } : {}),
    ...(opt(d.phone) ? { phone: opt(d.phone)! } : {}),
    ...(opt(d.website) ? { website: opt(d.website)! } : {}),
    ...(d.identifierScheme && opt(d.identifierValue)
      ? { identifierScheme: d.identifierScheme, identifierValue: opt(d.identifierValue)! }
      : {}),
    ...(opt(d.eori) ? { eori: opt(d.eori)! } : {}),
    ...(opt(d.mandateValidFrom) ? { mandateValidFrom: opt(d.mandateValidFrom)! } : {}),
    ...(opt(d.mandateValidUntil) ? { mandateValidUntil: opt(d.mandateValidUntil)! } : {}),
  };
}

/**
 * Economic operators the org's passports name (manufacturer, importer,
 * authorised representative, …). One default per role; products may override.
 * Writes are org-admin only — the API enforces it, the card mirrors it.
 */
export function OperatorsCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const t = useT();
  const toast = useToast();
  const [items, setItems] = useState<EconomicOperator[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EconomicOperator | null>(null);
  const [draft, setDraft] = useState<OperatorDraft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<unknown>(null);

  const load = async () => {
    setError(null);
    try {
      setItems(await listOperators(orgId));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    void load();
  }, [orgId]);

  const openNew = () => {
    setEditing(null);
    setDraft(EMPTY);
    setFormError(null);
    setOpen(true);
  };
  const openEdit = (o: EconomicOperator) => {
    setEditing(o);
    setDraft(draftOf(o));
    setFormError(null);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const body = bodyOf(draft);
      if (editing) await updateOperator(orgId, editing.id, body);
      else await createOperator(orgId, body);
      toast.success(t("operators.saved"));
      setOpen(false);
      await load();
    } catch (e) {
      setFormError(e);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    setError(null);
    try {
      await deleteOperator(orgId, id);
      toast.success(t("operators.deleted"));
      await load();
    } catch (e) {
      setError(e);
    }
  };

  const roleOptions = OPERATOR_ROLES.map((r) => ({ label: t(`operatorRole.${r}`), value: r }));
  const schemeOptions = [
    { label: t("operators.noIdentifier"), value: "" },
    ...OPERATOR_IDENTIFIER_SCHEMES.map((s) => ({ label: t(`operatorScheme.${s}`), value: s })),
  ];

  return (
    <Card
      title={t("operators.title")}
      extra={
        canWrite ? (
          <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={openNew}>
            {t("operators.new")}
          </Button>
        ) : undefined
      }
    >
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        <Text small secondary>
          {t("operators.hint")}
        </Text>
        {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
        <Table
          rowKey={(o) => o.id}
          empty={t("operators.empty")}
          columns={[
            {
              key: "role",
              title: t("operators.role"),
              render: (o: EconomicOperator) => (
                <Space size="sm">
                  <Tag color="primary">{t(`operatorRole.${o.role}`)}</Tag>
                  {o.isDefault && <Tag color="success">{t("operators.default")}</Tag>}
                  {o.isSelf && <Tag>{t("operators.self")}</Tag>}
                </Space>
              ),
            },
            {
              key: "name",
              title: t("operators.legalName"),
              render: (o: EconomicOperator) => (
                <span>
                  {o.legalName}
                  {o.tradeName ? <Text secondary> ({o.tradeName})</Text> : null}
                </span>
              ),
            },
            {
              key: "location",
              title: t("facilities.location"),
              render: (o: EconomicOperator) => [o.locality, o.countryIso].filter(Boolean).join(", "),
            },
            {
              key: "identifier",
              title: t("operators.identifier"),
              render: (o: EconomicOperator) =>
                o.identifierScheme && o.identifierValue ? (
                  <Text code>
                    {o.identifierScheme} {o.identifierValue}
                  </Text>
                ) : (
                  "—"
                ),
            },
            {
              key: "tier",
              title: t("operators.contactTier"),
              render: (o: EconomicOperator) => (
                <Tag color={o.contactTier === "public" ? "success" : "default"}>
                  {t(`operators.tier.${o.contactTier}`)}
                </Tag>
              ),
            },
            ...(canWrite
              ? [
                  {
                    key: "actions",
                    title: t("common.actions"),
                    render: (o: EconomicOperator) => (
                      <Space size="sm">
                        <Button size="sm" onClick={() => openEdit(o)}>
                          {t("common.edit")}
                        </Button>
                        <Popconfirm
                          title={t("operators.deleteConfirm")}
                          okType="danger"
                          okText={t("common.remove")}
                          cancelText={t("common.cancel")}
                          onConfirm={() => void remove(o.id)}
                        >
                          <Button size="sm" variant="danger">
                            {t("common.remove")}
                          </Button>
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
          data={items}
        />
      </Space>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? t("operators.edit") : t("operators.new")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        onOk={() => void save()}
        confirmLoading={saving}
        width={680}
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {formError != null && <ErrorAlert error={formError} />}
          <Space wrap>
            <div style={{ width: 260 }}>
              <Select
                label={t("operators.role")}
                options={roleOptions}
                value={draft.role}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    role: v as OperatorRole,
                    contactTier: PUBLIC_BY_DEFAULT.includes(v as OperatorRole) ? "public" : "restricted",
                  })
                }
              />
            </div>
            <Switch
              checked={draft.isDefault}
              onChange={(e) => setDraft({ ...draft, isDefault: e.target.checked })}
            >
              {t("operators.setDefault")}
            </Switch>
          </Space>
          <Input
            label={t("operators.legalName")}
            required
            value={draft.legalName}
            onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
          />
          <Input
            label={t("operators.tradeName")}
            value={draft.tradeName}
            onChange={(e) => setDraft({ ...draft, tradeName: e.target.value })}
          />
          <Input
            label={t("operators.streetAddress")}
            value={draft.streetAddress}
            onChange={(e) => setDraft({ ...draft, streetAddress: e.target.value })}
          />
          <Space wrap>
            <Input
              label={t("operators.postalCode")}
              value={draft.postalCode}
              onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })}
            />
            <Input
              label={t("facilities.city")}
              value={draft.locality}
              onChange={(e) => setDraft({ ...draft, locality: e.target.value })}
            />
            <Input
              label={t("operators.region")}
              value={draft.region}
              onChange={(e) => setDraft({ ...draft, region: e.target.value })}
            />
            <div style={{ width: 240 }}>
              <CountrySelect
                label={t("facilities.country")}
                required
                placeholder={t("facilities.countryPlaceholder")}
                value={draft.countryIso}
                onChange={(v) => setDraft({ ...draft, countryIso: v })}
              />
            </div>
          </Space>
          <Space wrap>
            <Input
              label={t("operators.email")}
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
            <Input
              label={t("operators.phone")}
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
            <Input
              label={t("operators.website")}
              placeholder="https://"
              value={draft.website}
              onChange={(e) => setDraft({ ...draft, website: e.target.value })}
            />
          </Space>
          <Space wrap>
            <div style={{ width: 220 }}>
              <Select
                label={t("operators.identifierScheme")}
                options={schemeOptions}
                value={draft.identifierScheme}
                onChange={(v) => setDraft({ ...draft, identifierScheme: v as OperatorIdentifierScheme | "" })}
              />
            </div>
            <Input
              label={t("operators.identifierValue")}
              help={t("operators.identifierHelp")}
              value={draft.identifierValue}
              onChange={(e) => setDraft({ ...draft, identifierValue: e.target.value })}
            />
            <Input
              label={t("operators.eori")}
              help={t("operators.eoriHelp")}
              value={draft.eori}
              onChange={(e) => setDraft({ ...draft, eori: e.target.value })}
            />
          </Space>
          <Space wrap>
            <div style={{ width: 220 }}>
              <Select
                label={t("operators.contactTier")}
                help={t("operators.contactTierHelp")}
                options={[
                  { label: t("operators.tier.public"), value: "public" },
                  { label: t("operators.tier.restricted"), value: "restricted" },
                ]}
                value={draft.contactTier}
                onChange={(v) => setDraft({ ...draft, contactTier: v as "public" | "restricted" })}
              />
            </div>
            {draft.contactTier === "restricted" && (
              <AudienceField value={draft.audience} onChange={(a) => setDraft({ ...draft, audience: a })} />
            )}
            {draft.role === "authorised_representative" && (
              <>
                <Input
                  label={t("operators.mandateFrom")}
                  placeholder="YYYY-MM-DD"
                  value={draft.mandateValidFrom}
                  onChange={(e) => setDraft({ ...draft, mandateValidFrom: e.target.value })}
                />
                <Input
                  label={t("operators.mandateUntil")}
                  placeholder="YYYY-MM-DD"
                  value={draft.mandateValidUntil}
                  onChange={(e) => setDraft({ ...draft, mandateValidUntil: e.target.value })}
                />
              </>
            )}
          </Space>
        </Space>
      </Modal>
    </Card>
  );
}
